import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import Dropzone from "react-dropzone";
import { ArrowRight, Bell, Camera, CheckCircle2, Clock3, FileCheck2, IdCard, ShieldCheck, TriangleAlert } from "lucide-react";
import api from "Common/api";
import { toast } from "react-toastify";

type InsuranceAnswer = "yes" | "no" | "unsure";
type ComplianceDocumentType = "identity" | "insurance" | "w9" | "license";

interface ComplianceDocument {
  document_type: ComplianceDocumentType;
  ai_status?: "pending" | "completed" | "failed" | string;
  verification_status: "verified" | "needs_review" | "expired" | string;
  holder_name?: string | null;
  business_name?: string | null;
  issuer?: string | null;
  document_number_last4?: string | null;
  expiration_date?: string | null;
  confidence?: number | string | null;
  issues?: string[] | string | null;
  view_url?: string | null;
}

interface ComplianceResponse {
  profile?: {
    has_business_insurance?: InsuranceAnswer;
    insurance_follow_up_status?: string | null;
  };
  documents?: ComplianceDocument[];
}

interface Props {
  onDocumentUpload: (type: ComplianceDocumentType, file: File) => Promise<unknown> | void;
  onInsuranceAnswerChange: (answer: InsuranceAnswer) => void;
  uploadingDocumentType?: string | null;
}

const documentLabels = {
  identity: "Government-issued ID",
  insurance: "Business insurance",
  w9: "W-9 tax form",
  license: "Contractor license",
};

const documentDescriptions: Record<ComplianceDocumentType, string> = {
  identity: "Driver’s license, state ID, or passport. The expiration date must be visible.",
  insurance: "Current Certificate of Insurance showing the carrier, coverage, and expiration date.",
  w9: "Latest completed and signed Form W-9. W-9 forms do not have an expiration date.",
  license: "Current state or local contractor license showing its status and expiration date.",
};

const statusMeta: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  pending: {
    label: "Verification pending",
    className: "is-pending",
    icon: <Clock3 size={15} />,
  },
  verified: {
    label: "AI checked",
    className: "is-verified",
    icon: <CheckCircle2 size={15} />,
  },
  expired: {
    label: "Expired",
    className: "is-expired",
    icon: <TriangleAlert size={15} />,
  },
  needs_review: {
    label: "Needs review",
    className: "is-review",
    icon: <Clock3 size={15} />,
  },
  rejected: {
    label: "Not verified",
    className: "is-rejected",
    icon: <TriangleAlert size={15} />,
  },
};

function parseIssues(value: ComplianceDocument["issues"]): string[] {
  if (Array.isArray(value)) return Array.from(new Set(value.map((item) => String(item || "").trim()).filter(Boolean)));
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? Array.from(new Set(parsed.map((item) => String(item || "").trim()).filter(Boolean)))
      : [];
  } catch (_) {
    return [];
  }
}

const documentGuideSteps: Record<ComplianceDocument["document_type"], string[]> = {
  identity: [
    "Use a current driver’s license, state ID, or passport.",
    "Photograph the full document—keep all four corners visible.",
    "Use bright, even light and avoid glare, blur, fingers, or shadows.",
    "Make sure the name matches the name on your contractor profile.",
  ],
  insurance: [
    "Upload the complete Certificate of Insurance (COI), not a payment receipt.",
    "Confirm the insured business name matches your company profile.",
    "Make sure the carrier, policy number, coverage, and expiration date are visible.",
    "Use a current policy that has not expired.",
  ],
  w9: [
    "Use the latest completed Form W-9.",
    "Make sure the legal name and business name are clearly readable.",
    "Include the federal tax classification and complete Part I.",
    "Upload a flat, clear image of the entire page without cropped edges.",
  ],
  license: [
    "Upload the contractor license issued by your state or local authority.",
    "Make sure the business or holder name matches your profile.",
    "Keep the license number, issuer, status, and expiration date visible.",
    "Use the renewed license if the current document has expired.",
  ],
};

function formatDate(value?: string | null) {
  if (!value) return "No expiration found";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "No expiration found"
    : `Expires ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

function presentIssue(issue: string) {
  const normalized = issue.trim().toLowerCase();
  const knownIssues: Record<string, string> = {
    blur: "The image is blurred and key details are not clearly readable.",
    "incorrect document type": "The uploaded file does not appear to be the required document type.",
    glare: "Glare or reflection is covering information on the document.",
    cropped: "Part of the document is cropped or outside the image.",
    expired: "The document is expired and must be replaced with a current copy.",
  };
  if (knownIssues[normalized]) return knownIssues[normalized];
  return issue.charAt(0).toUpperCase() + issue.slice(1);
}

const ContractorCompliancePanel: React.FC<Props> = ({
  onDocumentUpload,
  onInsuranceAnswerChange,
  uploadingDocumentType,
}) => {
  const [data, setData] = useState<ComplianceResponse>({});
  const [loading, setLoading] = useState(true);
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [consent, setConsent] = useState(false);
  const [guideDocument, setGuideDocument] = useState<{ type: ComplianceDocument["document_type"]; document: ComplianceDocument } | null>(null);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadedDocumentType, setUploadedDocumentType] = useState<ComplianceDocumentType>("identity");
  const previousStatuses = useRef<Partial<Record<ComplianceDocumentType, string | null>>>({});

  const loadCompliance = useCallback(async () => {
    try {
      const response = (await api.get("/company/compliance")) as unknown as ComplianceResponse;
      setData(response || {});
      (["identity", "insurance", "w9", "license"] as ComplianceDocumentType[]).forEach((type) => {
        const status = response?.documents?.find((item) => item.document_type === type)?.verification_status || null;
        if (previousStatuses.current[type] === "pending" && status && status !== "pending") {
          if (status === "verified") {
            toast.success(`${documentLabels[type]} has been AI checked and accepted.`);
          } else {
            toast.error(`${documentLabels[type]} could not be verified. Review the findings and upload a valid document.`);
          }
        }
        previousStatuses.current[type] = status;
      });
      const answer = response?.profile?.has_business_insurance || "unsure";
      onInsuranceAnswerChange(answer);
    } catch (error) {
      console.error("Unable to load contractor compliance", error);
    } finally {
      setLoading(false);
    }
  }, [onInsuranceAnswerChange]);

  useEffect(() => {
    loadCompliance();
    const refresh = () => loadCompliance();
    window.addEventListener("contractor-compliance-updated", refresh);
    return () => window.removeEventListener("contractor-compliance-updated", refresh);
  }, [loadCompliance]);

  const hasPendingVerification = Boolean(
    data.documents?.some((document) => document.ai_status === "pending" || document.verification_status === "pending")
  );

  useEffect(() => {
    if (!hasPendingVerification) return undefined;
    const timer = window.setInterval(loadCompliance, 4000);
    return () => window.clearInterval(timer);
  }, [hasPendingVerification, loadCompliance]);

  const answer = data.profile?.has_business_insurance || "unsure";
  const documents = useMemo(() => {
    const rows = new Map((data.documents || []).map((item) => [item.document_type, item]));
    return (["identity", "insurance", "w9", "license"] as const).map((type) => ({
      type,
      document: rows.get(type),
    }));
  }, [data.documents]);

  const completedCount = documents.filter(({ document }) => document?.verification_status === "verified").length;
  const activeDocument = documents.find(({ type }) => type === uploadedDocumentType)?.document;
  const activePending = activeDocument?.ai_status === "pending" || activeDocument?.verification_status === "pending";
  const verificationInProgress = Boolean(
    uploadingDocumentType === uploadedDocumentType ||
    activePending ||
    previousStatuses.current[uploadedDocumentType] === "pending"
  );

  const guideIssues = guideDocument ? parseIssues(guideDocument.document.issues) : [];
  const resolvedGuideIssues = guideIssues.length
    ? guideIssues.map(presentIssue)
    : guideDocument?.document.verification_status === "expired"
      ? ["This document appears to be expired."]
      : ["We could not confirm every required detail from this document."];

  const openGuide = (type: ComplianceDocument["document_type"], document?: ComplianceDocument) => {
    if (!document || !["needs_review", "expired", "rejected"].includes(document.verification_status)) return;
    setGuideDocument({ type, document });
  };

  const goToUpload = () => {
    if (!guideDocument) return;
    const targetId = `company-${guideDocument.type}-upload`;
    setGuideDocument(null);
    window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  };

  const saveInsuranceAnswer = async (nextAnswer: InsuranceAnswer) => {
    if (nextAnswer === "no" && !consent) {
      toast.info("Please authorize our insurance team to contact you.");
      return;
    }
    setSavingAnswer(true);
    try {
      const response = (await api.patch("/company/compliance/insurance", {
        answer: nextAnswer,
        consent: nextAnswer === "no" ? consent : false,
      })) as unknown as ComplianceResponse;
      setData(response);
      onInsuranceAnswerChange(nextAnswer);
      toast.success(nextAnswer === "no" ? "Our insurance team will follow up with you." : "Insurance preference saved.");
    } catch (error) {
      toast.error("We could not save your insurance preference.");
    } finally {
      setSavingAnswer(false);
    }
  };

  const uploadDocument = async (type: ComplianceDocumentType, file: File) => {
    setUploadedFileName(file.name);
    setUploadedDocumentType(type);
    setVerificationModalOpen(true);
    previousStatuses.current[type] = "pending";
    try {
      await onDocumentUpload(type, file);
      await loadCompliance();
    } catch (_) {
      previousStatuses.current[type] = null;
      setVerificationModalOpen(false);
    }
  };

  return (
    <>
    <section className="contractor-compliance" aria-labelledby="contractor-compliance-title">
      <div className="contractor-compliance__hero">
        <div className="contractor-compliance__hero-icon"><ShieldCheck size={24} /></div>
        <div className="contractor-compliance__hero-copy">
          <div className="contractor-compliance__eyebrow">Contractor compliance</div>
          <h3 id="contractor-compliance-title">Keep your business verified</h3>
          <p>Securely add your U.S. identity, tax, insurance, and license documents. AI checks key details and expiration dates; unusual documents go to manual review.</p>
        </div>
        <div className="contractor-compliance__score">
          <strong>{loading ? "—" : `${completedCount}/4`}</strong>
          <span>AI checked</span>
        </div>
      </div>

      <div className="contractor-compliance__status-grid">
        {documents.map(({ type, document }) => {
          const status = document?.verification_status || "not_submitted";
          const meta = statusMeta[status];
          const hasIssue = Boolean(document && ["needs_review", "expired", "rejected"].includes(status));
          const last4 = document?.document_number_last4 && document.document_number_last4 !== "null"
            ? document.document_number_last4
            : null;
          return (
            <div
              className={`contractor-compliance__status-card${hasIssue ? " is-actionable" : ""}`}
              key={type}
              role={hasIssue ? "button" : undefined}
              tabIndex={hasIssue ? 0 : undefined}
              aria-label={hasIssue ? `View problems and upload guide for ${documentLabels[type]}` : undefined}
              onClick={() => openGuide(type, document)}
              onKeyDown={(event) => {
                if (hasIssue && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  openGuide(type, document);
                }
              }}
            >
              <div className="contractor-compliance__status-top">
                <span className="contractor-compliance__doc-icon">
                  {type === "identity" ? <IdCard size={18} /> : <FileCheck2 size={18} />}
                </span>
                {meta ? (
                  <Badge className={`contractor-compliance__badge ${meta.className}`}>{meta.icon}{meta.label}</Badge>
                ) : (
                  <Badge className="contractor-compliance__badge is-missing">Not submitted</Badge>
                )}
              </div>
              <strong>{documentLabels[type]}</strong>
              <span>{document ? formatDate(document.expiration_date) : "Add a document below"}</span>
              {last4 && <small>Number ending in •••• {last4}</small>}
              {document?.view_url && (
                <a
                  className="contractor-compliance__view-document"
                  href={document.view_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  View document
                </a>
              )}
              {hasIssue && <small className="contractor-compliance__view-guide">View problems &amp; guide <ArrowRight size={12} /></small>}
            </div>
          );
        })}
      </div>

      <div className="contractor-compliance__insurance-question">
        <div>
          <h4>Do you have valid business insurance?</h4>
          <p>We’ll only request a Certificate of Insurance if you select Yes.</p>
        </div>
        <div className="contractor-compliance__answer-buttons" role="group" aria-label="Business insurance status">
          <Button variant={answer === "yes" ? "primary" : "outline-secondary"} onClick={() => saveInsuranceAnswer("yes")} disabled={savingAnswer}>Yes</Button>
          <Button variant={answer === "no" ? "primary" : "outline-secondary"} onClick={() => answer === "no" ? undefined : setData((current) => ({ ...current, profile: { ...current.profile, has_business_insurance: "no" } }))} disabled={savingAnswer}>No</Button>
        </div>
      </div>

      {answer === "no" && (
        <Alert variant="info" className="contractor-compliance__help-alert">
          <div>
            <strong>We can help you get covered.</strong>
            <p>Authorize our U.S. insurance team to contact you about business coverage options.</p>
          </div>
          {data.profile?.insurance_follow_up_status === "requested" ? (
            <Badge bg="info">Follow-up requested</Badge>
          ) : (
            <div className="contractor-compliance__consent">
              <Form.Check
                type="checkbox"
                id="insurance-team-consent"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                label="I agree to be contacted about insurance coverage."
              />
              <Button size="sm" onClick={() => saveInsuranceAnswer("no")} disabled={!consent || savingAnswer}>
                {savingAnswer ? <Spinner size="sm" /> : "Request help"}
              </Button>
            </div>
          )}
        </Alert>
      )}

      <div className="contractor-compliance__document-uploads">
        {documents
          .filter(({ type }) => type !== "insurance" || answer === "yes")
          .map(({ type, document }) => {
            const isUploading = uploadingDocumentType === type;
            const isPending = document?.ai_status === "pending" || document?.verification_status === "pending";
            return (
              <div className="contractor-compliance__identity-upload" id={`company-${type}-upload`} key={type}>
                <div>
                  <span className="contractor-compliance__step">
                    {type === "identity" ? "Required identity check" : "Secure AI document check"}
                  </span>
                  <h4>{documentLabels[type]}</h4>
                  <p>{documentDescriptions[type]} Sensitive numbers are retained only as the last four characters.</p>
                </div>
                <Dropzone
                  maxFiles={1}
                  maxSize={15 * 1024 * 1024}
                  accept={{ "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "application/pdf": [".pdf"] }}
                  onDrop={(files: File[]) => files[0] && uploadDocument(type, files[0])}
                  onDropRejected={() => toast.error("Upload one PNG, JPG, JPEG, or PDF file up to 15 MB.")}
                  disabled={Boolean(uploadingDocumentType || isPending)}
                >
                  {({ getRootProps, getInputProps }: any) => (
                    <button
                      type="button"
                      className="contractor-compliance__upload-button"
                      {...getRootProps()}
                      disabled={Boolean(uploadingDocumentType || isPending)}
                    >
                      <input {...getInputProps()} />
                      {isUploading || isPending ? <Spinner size="sm" /> : type === "identity" ? <IdCard size={19} /> : <FileCheck2 size={19} />}
                      {isUploading ? "Uploading…" : isPending ? "Checking…" : document ? "Replace document" : "Upload document"}
                    </button>
                  )}
                </Dropzone>
              </div>
            );
          })}
      </div>

      {documents.some(({ document }) => document?.verification_status === "expired") && (
        <Alert variant="warning" className="mt-3 mb-0">
          Your document appears to be expired. Don’t worry—we’re here to help. Please upload the renewed document to keep your profile verified.
        </Alert>
      )}
      {documents.flatMap(({ document }) => parseIssues(document?.issues)).length > 0 && (
        <p className="contractor-compliance__review-note">Some details need review. Your document remains securely saved while our team checks it.</p>
      )}
    </section>

    <Modal
      show={verificationModalOpen}
      onHide={() => setVerificationModalOpen(false)}
      centered
      dialogClassName="contractor-verification-modal"
      aria-labelledby="contractor-verification-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="contractor-verification-title">
          {verificationInProgress ? "Verification running in the background" : activeDocument?.verification_status === "verified" ? "Document verified" : "Verification update"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={`contractor-verification-modal__icon${verificationInProgress ? " is-pending" : activeDocument?.verification_status === "verified" ? " is-verified" : " is-rejected"}`}>
          {verificationInProgress ? <Spinner animation="border" size="sm" /> : activeDocument?.verification_status === "verified" ? <CheckCircle2 size={25} /> : <TriangleAlert size={25} />}
        </div>
        {verificationInProgress ? (
          <>
            <h4>Your {documentLabels[uploadedDocumentType].toLowerCase()} was uploaded securely</h4>
            <p>Our document-specific AI scan is running in the background. You can close this window and continue using RELiBUILD—we’ll notify you when it is verified or if it needs attention.</p>
            {uploadedFileName && <span className="contractor-verification-modal__file"><FileCheck2 size={16} />{uploadedFileName}</span>}
            <Alert variant="info" className="mb-0"><Bell size={16} /> No need to upload the document again while verification is pending.</Alert>
          </>
        ) : activeDocument?.verification_status === "verified" ? (
          <><h4>Your document is verified</h4><p>The document passed its type-specific automated checks and has been accepted.</p></>
        ) : (
          <><h4>We could not verify this document</h4><p>Wrong-type, sample, edited, mismatched, expired, or unreadable documents are not accepted. Open the findings and upload a valid replacement.</p></>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setVerificationModalOpen(false)}>{verificationInProgress ? "Continue" : "Done"}</Button>
      </Modal.Footer>
    </Modal>

    <Modal
      show={Boolean(guideDocument)}
      onHide={() => setGuideDocument(null)}
      centered
      size="lg"
      dialogClassName="contractor-guide-modal"
      aria-labelledby="contractor-guide-title"
    >
      <Modal.Header closeButton className="contractor-guide-modal__header">
        <div className="contractor-guide-modal__heading">
          <div>
            <Modal.Title id="contractor-guide-title">
              {guideDocument ? documentLabels[guideDocument.type] : "Document"}
            </Modal.Title>
            <span className="contractor-guide-modal__document-name">Compliance review · Action required</span>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="contractor-guide-modal__body">
        <div className="contractor-guide-modal__summary">
          <span className={`contractor-guide-modal__status ${guideDocument?.document.verification_status === "expired" ? "is-expired" : "is-review"}`}>
            <TriangleAlert size={17} />
            {guideDocument?.document.verification_status === "expired" ? "Expired document" : "Action required"}
          </span>
          <p>We could not verify this document automatically. Review the findings below and submit a corrected copy for another verification check.</p>
        </div>

        <div className="contractor-guide-modal__grid">
          <section className="contractor-guide-modal__panel is-problems">
            <div className="contractor-guide-modal__panel-title">
              <TriangleAlert size={18} />
              <div><span>Review findings</span><small>{resolvedGuideIssues.length} issue{resolvedGuideIssues.length === 1 ? "" : "s"} detected</small></div>
            </div>
            <ul className="contractor-guide-modal__issue-list">
              {resolvedGuideIssues.map((issue) => (
                <li key={issue}><TriangleAlert size={15} /><span>{issue}</span></li>
              ))}
            </ul>
          </section>

          <section className="contractor-guide-modal__panel is-guide">
            <div className="contractor-guide-modal__panel-title">
              <Camera size={18} />
              <div><span>Submission requirements</span><small>Checklist for successful verification</small></div>
            </div>
            <ol className="contractor-guide-modal__steps">
              {(guideDocument ? documentGuideSteps[guideDocument.type] : []).map((step, index) => (
                <li key={step}><span>{index + 1}</span><p>{step}</p></li>
              ))}
            </ol>
          </section>
        </div>

        <div className="contractor-guide-modal__tip">
          <CheckCircle2 size={18} />
          <div><strong>File quality requirements</strong><span>Submit the original PDF or a clear color image. Accepted formats: PDF, PNG, JPG, and JPEG; maximum size 15 MB.</span></div>
        </div>
      </Modal.Body>
      <Modal.Footer className="contractor-guide-modal__footer">
        <Button variant="light" onClick={() => setGuideDocument(null)}>Close</Button>
        <Button onClick={goToUpload}>Replace document <ArrowRight size={16} /></Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ContractorCompliancePanel;
